// ABOUTME: Desktop navigation bar with tabs, dropdown subroutes, and overflow "More" menu
// ABOUTME: Hidden on mobile via max-md:hidden, rendered inside the Navigation shell
import { Tab } from "@hm/fundamental"
import React from "react"
import { useTranslation } from "react-i18next"

import { useNavigationContext } from "../NavigationContext"
import { DropdownTab } from "../dropdown-tab"
import { MoreMenu } from "../more-menu"
import type { NavigationTabsProps } from "../types"

interface DesktopNavProps {
	navigationTabs: NavigationTabsProps[] | undefined
	leftSideButtons?: React.ReactNode
	rightSideButtons?: React.ReactNode
	children?: React.ReactNode
}

export const DesktopNav: React.FC<DesktopNavProps> = ({
	navigationTabs,
	leftSideButtons,
	rightSideButtons,
	children,
}) => {
	const { t } = useTranslation("shared", { keyPrefix: "navigation" })
	const {
		visibleTabs,
		overflowMenuItems,
		shouldShowMore,
		isMoreMenuOpened,
		openDropdown,
		convertSubroutesToMenuItems,
		toggleDropdown,
		closeAllDropdowns,
		toggleMoreMenu,
		desktopContainerRef,
		tabsPanelRef,
		leftButtonsRef,
		rightButtonsRef,
		logoRef,
		moreButtonRef,
		moreMenuRef,
		tabDropdownRefs,
	} = useNavigationContext()

	return (
		<div
			className="max-md:hidden flex w-full items-center justify-between"
			ref={desktopContainerRef}
		>
			<div className="flex items-center min-w-0 flex-1">
				{leftSideButtons && (
					<div
						ref={leftButtonsRef}
						className="align-middle gap-x-[10px] mr-12px inline-flex flex-shrink-0"
					>
						{leftSideButtons}
					</div>
				)}

				<div
					ref={logoRef}
					className="inline-block align-middle flex-shrink-0"
				>
					{children}
				</div>

				{navigationTabs && (
					<div className="flex items-center gap-x-8 ml-32px mr-32px min-w-0 relative">
						<div
							ref={tabsPanelRef}
							className="min-w-0"
							style={{
								maxWidth: shouldShowMore
									? "calc(100% - 64px)"
									: "100%",
							}}
						>
							<div className="py-12px px-16px flex gap-x-8 w-full">
								{visibleTabs.map(
									({ label, active, onClick, subroutes }) => {
										if (subroutes && subroutes.length > 0) {
											return (
												<DropdownTab
													key={label}
													label={label}
													active={active}
													menuItems={convertSubroutesToMenuItems(
														subroutes,
													)}
													isOpen={
														openDropdown === label
													}
													onToggle={() =>
														toggleDropdown(label)
													}
													dropdownRef={el => {
														tabDropdownRefs.current[
															label
														] = el
													}}
													onClose={closeAllDropdowns}
												/>
											)
										}

										return (
											<div
												key={label}
												data-tab-item
												className="flex-shrink-0"
											>
												<Tab
													label={label}
													onSelect={() => onClick?.()}
													isActive={active}
													className="whitespace-nowrap flex-shrink-0"
													data-cy={`tab-${label}`}
												/>
											</div>
										)
									},
								)}
							</div>
						</div>

						{shouldShowMore && (
							<MoreMenu
								label={t("moreLabel")}
								menuItems={overflowMenuItems}
								isOpen={isMoreMenuOpened}
								onToggle={toggleMoreMenu}
								buttonRef={moreButtonRef}
								menuRef={moreMenuRef}
							/>
						)}
					</div>
				)}
			</div>

			{rightSideButtons && (
				<div
					ref={rightButtonsRef}
					className="flex gap-x-[10px] flex-shrink-0 ml-32px"
				>
					{rightSideButtons}
				</div>
			)}
		</div>
	)
}
